import { formatNumber, useTranslation } from '@hbs-us/ui';

import { EvilBarChart } from '../../../components/charts/charts/recharts-bar-chart.js';
import type { ChartConfig } from '../../../components/charts/ui/recharts-chart.js';
import type { OverviewEstimateStats } from '../overviewTypes.js';

import { HomeCard } from './HomeCard.js';

/**
 * The estimate funnel: how many quotes sit at each stage.
 *
 * THIS USED TO DRAW ITS OWN SVG — four `<rect>` elements whose widths were percentages of a
 * hand-picked 260-unit viewBox. It is a bar chart, so it is now a bar chart, with an axis and a
 * hover tooltip it never had.
 *
 * ⚠ `layout="vertical"` NAMES THE BARS, NOT THE RECHARTS AXIS, and the two are opposites: it gives
 * vertical COLUMNS with the stage on the X axis and the count on the Y. `layout="horizontal"` was
 * tried first and drew NOTHING — not a short bar, no bar layer in the DOM at all — because the
 * categorical axis moves to Y in that mode and the `dataKey` was still declared on the X axis. The
 * axes rendered, the grid rendered, and every test stayed green. Count the painted marks in a real
 * browser; a chart that mounts is not a chart that draws.
 *
 * ONE SERIES, ONE COLOUR. The `colors` array in a config entry is the gradient ramp for that
 * series, not a per-bar palette, so four differently coloured stages would need four series over
 * one row each. The descent in the counts is the message here; the colour was never carrying it.
 * The stage list below therefore lost its coloured dots too — they were a legend for a colour
 * distinction that no longer exists.
 */

type StageTickProps = {
  x?: number | string;
  y?: number | string;
  index?: number;
  fill?: string;
  payload?: { value?: string };
};

/**
 * Stage names on two alternating lines. The card is the narrow third column, so four names get
 * about 44px each, and a single long word cannot wrap: "Sent" and "Converted" sat 3px apart in
 * English and "Enviados" ran into "Convertidos" in Spanish. Every other name drops a line, which
 * fits any language without shortening the words.
 */
function StageTick({ x, y, index = 0, fill, payload }: StageTickProps) {
  return (
    <text
      x={Number(x)}
      y={Number(y)}
      dy={index % 2 === 0 ? 10 : 24}
      textAnchor="middle"
      fontSize={11}
      fill={fill}
    >
      {payload?.value}
    </text>
  );
}

export function EstimatePipeline({
  stats,
  money,
}: {
  stats: OverviewEstimateStats;
  money: (value: number) => string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  // Built here so the tooltip prints a translated series name, not the literal key.
  const config = {
    count: {
      label: t('home:pipeline.openEstimatesLabel'),
      colors: { light: ['var(--chart-3)'], dark: ['var(--chart-3)'] },
    },
  } satisfies ChartConfig;

  /*
    TWO NAMES PER STAGE, and the reason is the axis. `interval={0}` is what stops Recharts silently
    dropping a tick it thinks will collide — it was hiding "Awaiting response", so the chart showed
    three labels under four bars and nobody could tell which bar was which. Forcing all four to
    render then makes the long one overlap its neighbours, which is worse. The axis gets the short
    name; the list under the chart, where there is room, keeps the full one.
  */
  const stages = [
    { label: t('home:pipeline.draft'), short: t('home:pipeline.draft'), count: stats.draft },
    { label: t('home:pipeline.ready'), short: t('home:pipeline.ready'), count: stats.ready },
    {
      label: t('home:pipeline.awaitingResponse'),
      short: t('home:pipeline.awaitingShort'),
      count: stats.awaitingResponse,
    },
    {
      label: t('home:pipeline.converted30d'),
      short: t('home:pipeline.converted30d'),
      count: stats.converted,
    },
  ];
  const chartData = stages.map((stage) => ({ label: stage.short, count: stage.count }));
  const openCount = stats.draft + stats.ready + stats.awaitingResponse;
  const funnelLabel = t('home:pipeline.chartLabel', {
    draft: stats.draft,
    ready: stats.ready,
    awaiting: stats.awaitingResponse,
    converted: stats.converted,
  });

  return (
    <HomeCard
      headingId="home-pipeline-heading"
      /* The description used to repeat `pipelineValue`, which is already the big number below it.
         One number, once. */
      title={t('home:pipeline.heading')}
      /* A label, not a pill with a caret on it: nothing here was ever clickable. */
      action={
        <span className="shrink-0 text-muted-foreground text-xs">{t('home:pipeline.window')}</span>
      }
    >
      <div className="mt-3 grid gap-4">
        <div>
          <div className="font-display font-semibold text-3xl text-foreground tabular-nums">
            {money(stats.pipelineValue)}
          </div>
          <div className="text-muted-foreground text-xs">
            {t('home:pipeline.openEstimates', {
              count: openCount,
              value: formatNumber(openCount, locale),
            })}
          </div>
        </div>

        <div aria-hidden="true">
          <EvilBarChart
            className="h-32 w-full"
            config={config}
            data={chartData}
            layout="vertical"
            xDataKey="label"
            /* Room on the right so the last stage name is not cut by the card edge. */
            chartProps={{ margin: { top: 4, right: 16, bottom: 0, left: 0 } }}
          >
            <EvilBarChart.Grid />
            {/*
              `interval={0}` or Recharts drops labels it thinks will collide, and it was dropping
              the third of four — the chart showed Draft, Ready and Converted and silently hid
              "Awaiting response". The stage names are short enough to all fit once it is told to
              keep them.
            */}
            <EvilBarChart.XAxis
              dataKey="label"
              interval={0}
              height={40}
              tick={(props: StageTickProps) => <StageTick {...props} />}
            />
            <EvilBarChart.YAxis width={40} allowDecimals={false} />
            <EvilBarChart.Tooltip />
            {/*
              Capped. Four categories across a full-width card gave bars 204px wide and 110px
              tall — wider than they were high, which reads as blocks rather than a series.
            */}
            <EvilBarChart.Bar
              dataKey="count"
              enableHoverHighlight
              barProps={{ dataKey: 'count', maxBarSize: 72 }}
            />
          </EvilBarChart>
        </div>

        <dl className="grid gap-2">
          {stages.map((stage) => (
            <div key={stage.label} className="flex items-center justify-between gap-3 text-sm">
              <dt className="min-w-0 truncate text-muted-foreground">{stage.label}</dt>
              <dd className="font-semibold text-foreground tabular-nums">
                {formatNumber(stage.count, locale)}
              </dd>
            </div>
          ))}
        </dl>
        <p className="sr-only">{funnelLabel}</p>
      </div>
    </HomeCard>
  );
}
